import React, { useState, useRef, useEffect } from 'react'; // Import useRef and useEffect
import "../Styles/Testimonial.css";
import { FaPlus } from "react-icons/fa";
import { BsDash } from "react-icons/bs";

const Testimonial = () => {
  // State variables for FAQ toggles
  const [isCustomWallpaperOpen, setIsCustomWallpaperOpen] = useState(false);
  const [isOrderCustomGraphicsOpen, setIsOrderCustomGraphicsOpen] = useState(false);
  const [isPreviewArtworkOpen, setIsPreviewArtworkOpen] = useState(false);
  const [isInternationalShippingOpen, setIsInternationalShippingOpen] = useState(false);
  const [isHandPaintingsOriginalOpen, setIsHandPaintingsOriginalOpen] = useState(false);
  const [isCollaborateWithDesignersOpen, setIsCollaborateWithDesignersOpen] = useState(false);
  const [isLargeProjectConsultationOpen, setIsLargeProjectConsultationOpen] = useState(false);
  const [isCustomProjectTimelineOpen, setIsCustomProjectTimelineOpen] = useState(false);
  const [isBulkOrderOpen, setIsBulkOrderOpen] = useState(false);

  // Toggle functions
  const toggleCustomWallpaper = () => setIsCustomWallpaperOpen(!isCustomWallpaperOpen);
  const toggleOrderCustomGraphics = () => setIsOrderCustomGraphicsOpen(!isOrderCustomGraphicsOpen);
  const togglePreviewArtwork = () => setIsPreviewArtworkOpen(!isPreviewArtworkOpen);
  const toggleInternationalShipping = () => setIsInternationalShippingOpen(!isInternationalShippingOpen);
  const toggleHandPaintingsOriginal = () => setIsHandPaintingsOriginalOpen(!isHandPaintingsOriginalOpen);
  const toggleCollaborateWithDesigners = () => setIsCollaborateWithDesignersOpen(!isCollaborateWithDesignersOpen);
  const toggleLargeProjectConsultation = () => setIsLargeProjectConsultationOpen(!isLargeProjectConsultationOpen);
  const toggleCustomProjectTimeline = () => setIsCustomProjectTimelineOpen(!isCustomProjectTimelineOpen);
  const toggleBulkOrder = () => setIsBulkOrderOpen(!isBulkOrderOpen);

  return (
    <div className="w-full">


      {/* Main Content Section - FAQ only */}
      <div className="w-full bg-gradient-to-b from-gray-50 to-white py-8 md:py-12 lg:py-16">
        <div className="max-w-xl md:max-w-3xl lg:max-w-4xl mx-auto px-4"> {/* Adjusted max-w to centralize FAQ */}
          <div className="flex flex-col items-center"> {/* Centered FAQ section */}

            {/* FAQ Section */}
            <div className="w-full"> {/* FAQ now takes full width in this section */}
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg w-full">
                <h2 className="text-3xl md:text-3xl font-serif text-gray-500 mb-6 text-center">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4"> {/* Increased space between FAQ items */}
                  <FAQItem
                    question="Can I request a custom wallpaper or artwork for my space?"
                    answer="Absolutely. We work closely with clients to create bespoke wallpapers or graphics that match your interior theme, mood, and dimensions."
                    isOpen={isCustomWallpaperOpen}
                    toggle={toggleCustomWallpaper}
                  />

                  <FAQItem
                    question="How can I place an order for custom designed graphics?"
                    answer="You can reach out through our website, call or what's app on given number. Share your brief, and our design team will contact you to discuss concepts and pricing."
                    isOpen={isOrderCustomGraphicsOpen}
                    toggle={toggleOrderCustomGraphics}
                  />

                  <FAQItem
                    question="Can I preview how the artwork or wallpaper will look in my room?"
                    answer="Yes, we offer digital mockups for custom orders to help you visualize the final look before production."
                    isOpen={isPreviewArtworkOpen}
                    toggle={togglePreviewArtwork}
                  />

                  <FAQItem
                    question="Do you ship internationally?"
                    answer="Yes, we offer international shipping for most products. Shipping costs and delivery times vary by location."
                    isOpen={isInternationalShippingOpen}
                    toggle={toggleInternationalShipping}
                  />

                  <FAQItem
                    question="Are your hand paintings original?"
                    answer="Yes, all our hand paintings are original and come with an authenticity certificate."
                    isOpen={isHandPaintingsOriginalOpen}
                    toggle={toggleHandPaintingsOriginal}
                  />

                  <FAQItem
                    question="Do you collaborate with interior designers or architects for custom graphics?"
                    answer="Yes, we frequently work with interior designers and architects to develop tailored graphics that seamlessly integrate into their project vision and space planning."
                    isOpen={isCollaborateWithDesignersOpen}
                    toggle={toggleCollaborateWithDesigners}
                  />

                  <FAQItem
                    question="Do you offer site visits or consultations for large interior projects?"
                    answer="Yes, for large or complex projects, we offer consultations virtually or on site (depending on location) to understand the space, measure accurately, and align on design direction."
                    isOpen={isLargeProjectConsultationOpen}
                    toggle={toggleLargeProjectConsultation}
                  />

                  <FAQItem
                    question="How long does it take to complete a custom graphic or wallpaper project?"
                    answer="Timelines vary based on complexity, but most custom projects are completed within 1–3 weeks, including concept development, revisions, and final production."
                    isOpen={isCustomProjectTimelineOpen}
                    toggle={toggleCustomProjectTimeline}
                  />

                  <FAQItem
                    question="Do you offer bulk or corporate orders for paintings and prints?"
                    answer="Yes, we provide bulk solutions for interior projects, offices, hospitality spaces, and corporate gifting. Custom themes, sizes, and pricing are available based on your requirements."
                    isOpen={isBulkOrderOpen}
                    toggle={toggleBulkOrder}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, toggle }) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('0px');

  useEffect(() => {
    if (isOpen && contentRef.current) {
      // Set max-height to the actual scroll height of the content plus some padding
      // This ensures all content is visible without a fixed cutoff.
      setContentHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setContentHeight('0px');
    }
  }, [isOpen]); // Recalculate height when isOpen changes

  return (
    <div className="bg-gray-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-gray-100">
      <button
        onClick={toggle}
        className="w-full px-4 py-3 text-left flex items-center justify-between font-medium text-gray-500 hover:text-gray-900 text-sm md:text-base"
      >
        <span>{question}</span>
        {isOpen ? (
          <BsDash className="text-blue-500 flex-shrink-0 ml-2" size={20} />
        ) : (
          <FaPlus className="text-blue-500 flex-shrink-0 ml-2" size={20} />
        )}
      </button>
      <div
        ref={contentRef} // Assign ref to the content div
        style={{ maxHeight: contentHeight }} // Apply dynamic max-height
        className={`overflow-hidden transition-all duration-300 ease-in-out`}
      >
        <p className="px-4 pb-3 text-gray-600 text-sm md:text-base sm:text-xs leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

export default Testimonial;